import { StringList } from '@trustero/trustero-api-web/lib/common/collections_pb'
import {
  RESPONSIBILITY,
  ResponsibilityValue,
} from '@trustero/trustero-api-web/lib/common/model_pb'
import {
  CONTROL_STATUS,
  ControlStatusValue,
  CreateUpdateControlRequest,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { Department } from '@trustero/trustero-api-web/lib/model/department_pb'
import { PolicyRecord } from '@trustero/trustero-api-web/lib/model/policy_pb'
import { VendorRecord } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import {
  Responsibility,
  ResponsibilityOptions,
  ResponsibilityToOptions,
} from 'src/components/PageLayout/ShowPage/Dropdowns/Dropdowns.constants'
import { ControlStatus } from 'src/lib/common/types'
import {
  controlStatusTitle,
  controlStatusToTitle,
} from 'src/lib/common/types/control'
import {
  createLowercaseLookupMap,
  CSV_COLUMN_VALIDATIONS,
  CSV_VALIDATIONS,
  HeaderValues,
  ParseColumnType,
  RowValidator,
} from 'src/Utils/csv/csv.constants'
import { NONE_ID, NONE_NAME } from 'src/Utils/globalConstants'
import { separateValues } from 'src/Utils/csv/csv.helpers'
import { ComplianceFrameworkGroup } from 'src/Utils/globalTypes'
import { getDepartmentPolicyMap } from './ControlModals.helpers'

export const CUSTOM_FRAMEWORK_INSTRUCTIONS = ` ("True" to add custom framework association)`

export const CONTROL_CSV_TEMPLATE: { [key: string]: string }[] = [
  {
    'Control ID': 'CTRL-0001',
    'Control Name': 'This is a sample control',
    Objective: 'This is a sample objective',
    Notes: 'This is a sample note',
    Procedure: 'This is a sample procedure',
    Policy: '',
    Department: '',
    Status: 'Needs Attention',
    Responsibility: 'Direct responsibility',
    Reason: 'None',
    Vendor: 'None',
    Assignee: 'Unassigned',
    'Required Evidence': 'sample required evidence',
    'Test Procedures': 'sample test procedures',
  },
  {
    'Control ID': 'CTRL-0002',
    'Control Name': 'This is a sample control',
    Objective: 'This is a sample objective',
    Notes: 'This is a sample note',
    Procedure: 'This is a sample procedure',
    Policy: '',
    Department: '',
    Status: 'Needs Attention',
    Responsibility: 'Direct responsibility',
    Reason: 'None',
    Vendor: 'None',
    Assignee: 'Unassigned',
    'Required Evidence': 'sample required evidence',
    'Test Procedures': 'sample test procedures',
  },
]

export interface ImportControlCSVRow {
  [key: string]: string
}
export const CONTROL_CSV_HEADERS: HeaderValues[] = [
  {
    input: 'Control ID',
    output: 'modelId',
  },
  {
    input: 'Control Name',
    output: 'name',
  },
  {
    input: 'Objective',
    output: 'objective',
  },
  {
    input: 'Notes',
    output: 'notes',
    optional: true,
  },
  {
    input: 'Procedure',
    output: 'procedure',
    optional: true,
  },
  {
    input: 'Policy',
    output: 'policy',
    optional: true,
  },
  {
    input: 'Department',
    output: 'department',
    optional: true,
  },
  {
    input: 'Status',
    output: 'status',
    optional: true,
  },
  {
    input: 'Responsibility',
    output: 'responsibility',
    optional: true,
  },
  {
    input: 'Reason',
    output: 'reason',
    optional: true,
  },
  {
    input: 'Vendor',
    output: 'vendor',
    optional: true,
  },
  {
    input: 'Assignee',
    output: 'assignee',
    optional: true,
  },
  {
    input: 'Required Evidence',
    output: 'requiredEvidence',
    optional: true,
  },
  {
    input: 'Test Procedures',
    output: 'testProcedures',
    optional: true,
  },
]

export const getControlParsers = (
  frameworks: { [key: string]: ComplianceFrameworkGroup },
  policies: PolicyRecord[],
  controlModelIds: string[] = [], // pass in control model ids when you want to ensure no duplicates,
  vendors: VendorRecord[] = [],
  departments: Department[] = [],
): ParseColumnType => {
  const validators: ParseColumnType = {
    modelId: {
      validator: (columnName, value, row) => {
        const lengthValidation = CSV_VALIDATIONS.LENGTH(
          columnName,
          value,
          1,
          20,
          row,
        )
        const valueValidation = CSV_VALIDATIONS.VALUE_NOT_IN_ARRAY(
          value,
          row,
          controlModelIds,
        )

        if (lengthValidation) return lengthValidation
        else if (valueValidation) return valueValidation
        else return ''
      },
      mutator: (value: string) => value.slice(0, 20),
    },
    name: {
      validator: (columnName, value, row) =>
        CSV_VALIDATIONS.LENGTH(columnName, value, 1, 255, row),
      mutator: (value: string) => value.slice(0, 255),
    },
    objective: {
      validator: (_c, _v, _r) => null,
      mutator: (value) => value,
    },
    policy: {
      validator: (_c, value, row) =>
        CSV_VALIDATIONS.VALUE_IN_ARRAY(value, row, [
          NONE_ID,
          ...policies.map((policy) => policy.getName().trim().toLowerCase()),
        ]),
      mutator: (value: string) => {
        if (value.trim().toLowerCase() === NONE_ID) return ''
        return value.slice(0, 255)
      },
    },
    procedure: {
      validator: (_c, _v, _r) => null,
      mutator: (value) => value,
    },
    notes: {
      validator: (_c, _v, _r) => null,
      mutator: (value) => value,
    },
    requiredEvidence: {
      validator: (_c, _v, _r) => null,
      mutator: (value) => value,
    },
    testProcedures: {
      validator: (_c, _v, _r) => null,
      mutator: (value) => value,
    },
    status: {
      validator: (_c, value, row) =>
        CSV_VALIDATIONS.VALUE_IN_ARRAY(
          value,
          row,
          Object.values(ControlStatus),
          Object.values(ControlStatus),
        ),
      mutator: (value) => {
        if (!value) return value

        const lowerCaseLookup = createLowercaseLookupMap(controlStatusToTitle)
        const adjustedValue = value.trim().toLowerCase()
        return adjustedValue in lowerCaseLookup
          ? `${lowerCaseLookup[adjustedValue]}`
          : value
      },
    },
    responsibility: {
      validator: (_c, value, row) =>
        CSV_VALIDATIONS.VALUE_IN_ARRAY(
          value,
          row,
          Object.values(Responsibility),
          Object.values(Responsibility),
        ),
      mutator: (value) => {
        if (!value) return value

        const lowerCaseLookup = createLowercaseLookupMap(
          ResponsibilityToOptions,
        )
        const adjustedValue = value.trim().toLowerCase()
        return adjustedValue in lowerCaseLookup
          ? `${lowerCaseLookup[adjustedValue]}`
          : value
      },
    },
    reason: {
      validator: (_c, _v, _r) => null, // anything goes
      mutator: (value) => value,
    },
    vendor: {
      validator: (_c, value, row) => {
        const vendorNames = [
          NONE_NAME,
          ...vendors.map((vendor) => vendor.getName()),
        ]
        return CSV_VALIDATIONS.VALUE_IN_ARRAY(
          value.trim().toLowerCase(),
          row,
          vendorNames,
          vendorNames,
        )
      },
      mutator: (value) => {
        if (!value) return value
        if (value.trim().toLowerCase() === NONE_NAME.toLowerCase()) {
          return ''
        }

        const updatedValue = value.trim().toLowerCase()

        return (
          vendors
            .find((vendor) => vendor.getName().toLowerCase() === updatedValue)
            ?.getId() || ''
        )
      },
    },
    department: {
      validator: (_c, value, row) => {
        const departmentNames = [
          NONE_NAME,
          ...departments.map((department) => department.getName()),
          ...departments.map((department) => department.getShortName()),
        ]
        return CSV_VALIDATIONS.VALUE_IN_ARRAY(value, row, departmentNames)
      },
      mutator: (value) => value,
    },
  }

  Object.entries(frameworks).forEach(([name, objectives]) => {
    const names: string[] = objectives.objectives.map((objective) => {
      return objective.getName()
    })
    validators[name] = {
      validator: (_c, value, row) =>
        CSV_VALIDATIONS.VALUES_IN_ARRAY(
          value,
          row,
          names,
          (value: string, candidate: string) => {
            return (
              candidate.startsWith(value) ||
              candidate.endsWith(value) ||
              value.includes(candidate)
            )
          },
        ),
      mutator: (value) => value,
    }
  })

  return validators
}

export const getControlRowValidators = (
  policies: PolicyRecord[],
  departments: Department[],
): RowValidator[] => {
  const departmentPolicyMap = getDepartmentPolicyMap(departments, policies)

  const validateResponsibility: RowValidator = (
    row: Record<string, string>,
    rowNumber: number,
  ): string | null => {
    const responsibilityIndex =
      Number(row.responsibility?.trim().toLowerCase() || '') || -1
    const responsibility =
      ResponsibilityOptions[responsibilityIndex as RESPONSIBILITY] || ''
    const statusIndex = Number(row.status?.trim().toLowerCase() || '') || -1
    const status = controlStatusTitle[statusIndex as CONTROL_STATUS] || ''
    const reason = row.reason?.trim() || ''
    const naResponsibility = Responsibility.NOT_APPLICABLE
    const directResponsibility = Responsibility.DIRECT_RESPONSIBILITY
    const inheritedResponsibility = Responsibility.INHERITED
    const outsourcedResponsibility = Responsibility.OUTSOURCED_ATTESTATION
    const outsourcedNoAttestationResponsibility =
      Responsibility.OUTSOURCED_NO_ATTESTATION
    const naStatus = controlStatusTitle[CONTROL_STATUS.NOTAPPLICABLE]
    const vendor = row.vendor?.trim().toLowerCase() || ''
    const naVendor = NONE_NAME.toLowerCase()

    if (responsibility && responsibility !== directResponsibility && !reason) {
      return `Row ${rowNumber}: Responsibility is "${responsibility}". When Responsibility is not "Direct Responsibility", Reason must be provided.`
    }
    if (responsibility === naResponsibility && status !== naStatus) {
      return `Row ${rowNumber}: Responsibility is "${responsibility}", status is "${status}". When responsibility is "Not Applicable", status will be set to "Not Applicable". Double check your status value.`
    }
    if (status === naStatus && responsibility !== naResponsibility) {
      return `Row ${rowNumber}: Responsibility is "${responsibility}", status is "${status}". Responsibility must be "Not Applicable" when status is "Not Applicable". Double check your responsibility value.`
    }
    if (
      responsibility === outsourcedResponsibility ||
      responsibility === outsourcedNoAttestationResponsibility ||
      responsibility === inheritedResponsibility
    ) {
      if (!vendor || vendor === naVendor) {
        return `Row ${rowNumber}: Responsibility is ${responsibility}. When Responsibility is "Outsourced - Attestation", "Outsourced - No Attestation", or "Inherited", Vendor must be provided.`
      }
    }
    return null
  }

  const validatePolicyAndDepartment: RowValidator = (
    row: Record<string, string>,
    rowNumber: number,
  ): string | null => {
    if (!row.department || row.department.trim().toLowerCase() === NONE_ID) {
      return null
    }
    if (!row.policy) {
      return `Row ${rowNumber}: Policy is required when department is provided.`
    }
    const policy = row.policy?.trim() || ''

    const department = row.department?.trim() || ''
    const departmentKey =
      Object.keys(departmentPolicyMap).find(
        (key) => key.toLowerCase() === department.toLowerCase(),
      ) || ''
    if (policy && departmentKey) {
      const policies = departmentPolicyMap[departmentKey] || []
      if (!policies.some((p) => p.toLowerCase() === policy.toLowerCase())) {
        return `Row ${rowNumber}: Policy "${policy}" does not belong to department "${department}".`
      }
    }
    return null
  }

  const validateDuplicateControlId = CSV_COLUMN_VALIDATIONS.DUPLICATE_VALUES(
    'modelId',
    'Control ID',
  )

  return [
    validateResponsibility,
    validatePolicyAndDepartment,
    validateDuplicateControlId,
  ]
}

export const prepareControlsUpload = (
  complianceFrameworks: string[],
  rows: ImportControlCSVRow[],
): CreateUpdateControlRequest[] => {
  return rows.map((row) => {
    const control = new CreateUpdateControlRequest()
      .setModelId(new StringValue().setValue(row.modelId))
      .setName(new StringValue().setValue(row.name))
      .setObjective(new StringValue().setValue(row.objective))
    if (row.policy) {
      control.setPolicyId(new StringValue().setValue(row.policy))
    }
    if (row.procedure) {
      control.setDescription(new StringValue().setValue(row.procedure))
    }
    if (row.notes) {
      control.setNote(new StringValue().setValue(row.notes))
    }
    if (row.requiredEvidence) {
      control.setRequiredEvidence(
        new StringValue().setValue(row.requiredEvidence),
      )
    }
    if (row.testProcedures) {
      control.setTestProcedures(new StringValue().setValue(row.testProcedures))
    }
    if (row.status) {
      control.setStatus(
        new ControlStatusValue().setValue(
          row.status as unknown as CONTROL_STATUS,
        ),
      )
    }
    if (row.responsibility) {
      control.setResponsibility(
        new ResponsibilityValue().setValue(
          row.responsibility as unknown as RESPONSIBILITY,
        ),
      )
    }
    if (row.reason) {
      control.setNotApplicableReason(new StringValue().setValue(row.reason))
    }
    if (row.vendor) {
      control.setVendorId(new StringValue().setValue(row.vendor))
    }

    control.setComplianceFrameworkIds(
      new StringList().setItemList(
        complianceFrameworks.filter((cFramework) => {
          return cFramework in row && row[cFramework] !== ''
        }),
      ),
    )

    control.setFrameworkIds(
      new StringList().setItemList(
        complianceFrameworks.flatMap((cFramework) => {
          if (cFramework in row && row[cFramework] !== '') {
            const values = separateValues(row[cFramework])
            return values.map((value) => {
              return `${cFramework}: ${value}`
            })
          } else {
            return []
          }
        }),
      ),
    )
    return control
  })
}

export const ERROR_MARKING_CONTROL_NA = 'Error marking control not applicable'
